import React from 'react';

import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import * as lucideIcons from 'lucide-react';


const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                outline: "border border-input hover:bg-accent hover:text-accent-foreground",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                success: "bg-success text-success-foreground hover:bg-success/90",
                warning: "bg-warning text-warning-foreground hover:bg-warning/90",
                danger: "bg-error text-error-foreground hover:bg-error/90",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10",
                xs: "h-8 rounded-md px-2 text-xs",
                xl: "h-12 rounded-md px-10 text-base",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

const Button = React.forwardRef(({ 
  children, 
  variant = 'default', 
  size = 'default',
  iconName,
  iconPosition = 'left',
  disabled = false,
  loading = false,
  className,
  ...props 
}, ref) => {
  const IconComponent = iconName ? lucideIcons?.[iconName] : null;

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, size }), className)}
      aria-busy={loading}
      {...props}>
      {loading && (
        <lucideIcons.Loader2 
          className="mr-2 h-4 w-4 animate-spin" 
          aria-hidden="true" />
      )}
      {IconComponent && !loading && iconPosition === 'left' && (
        <IconComponent className="mr-2 h-4 w-4" aria-hidden="true" />
      )}
      {children}
      {IconComponent && !loading && iconPosition === 'right' && (
        <IconComponent className="ml-2 h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
});

Button.displayName = "Button";
export default Button;